import { forwardRef, type ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-blue text-white hover:bg-blue-deep",
  secondary: "border border-edge bg-board text-ink hover:bg-ice-2",
  danger: "bg-red text-white hover:bg-red-deep",
  ghost: "text-blue hover:bg-blue-wash",
};

/** Minimum heights keep touch targets at 44px on phones (md and lg); sm is for dense desktop tables. */
const SIZES: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 text-note",
  md: "min-h-11 px-4 text-operate",
  lg: "min-h-12 px-5 text-lg",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks the button while work is in flight. The label stays visible. */
  loading?: boolean;
}

/** Forwards its ref so drag libraries (dnd-kit activators) can attach to the real button. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    className = "",
    type = "button",
    disabled,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-control font-semibold",
        "transition-colors duration-[120ms] disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className,
      ].join(" ")}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      ) : null}
      {children}
    </button>
  );
});
